import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import './Admin.css';

const UserBlockManagement = () => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [blockedUsers, setBlockedUsers] = useState([]);
  const [missedClassUsers, setMissedClassUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showBlocked, setShowBlocked] = useState(false);
  const [showMissedClasses, setShowMissedClasses] = useState(false);
  const [searchPerformed, setSearchPerformed] = useState(false);

  // Fetch blocked users on component mount
  useEffect(() => {
    fetchBlockedUsers();
  }, []);

  // Fetch users with missed classes on component mount
  useEffect(() => {
    if (showMissedClasses) {
      fetchMissedClassUsers();
    }
  }, [showMissedClasses]);

  // Function to fetch all blocked users
  const fetchBlockedUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('blockuser/blocked', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBlockedUsers(response.data.users || []);
    } catch (err) {
      console.error('Error fetching blocked users:', err);
      setError(err.response?.data?.error?.message || t('error_fetching_blocked_users'));
    } finally {
      setLoading(false);
    }
  };

  // Function to fetch users sorted by missed class count
  const fetchMissedClassUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('blockuser/missed-classes', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data && response.data.success) {
        setMissedClassUsers(response.data.users || []);
      } else {
        console.error('Unexpected response format:', response.data);
        setError(t('error_fetching_missed_class_users'));
        setMissedClassUsers([]);
      }
    } catch (err) {
      console.error('Error fetching missed class users:', err);
      setError(err.response?.data?.error?.message || t('error_fetching_missed_class_users'));
      setMissedClassUsers([]);
    } finally {
      setLoading(false);
    }
  };

  // Debounced search function
  const debouncedSearch = useCallback(
    (() => {
      let timeoutId = null;
      return (term) => {
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
        timeoutId = setTimeout(() => {
          if (term.length > 0) {
            searchUsers(term);
          } else {
            setSearchResults([]);
            setSearchPerformed(false);
            setError(null);
          }
        }, 300); // 300ms delay
      };
    })(),
    [/* No dependencies needed here */]
  );

  // Update search when searchTerm changes
  useEffect(() => {
    debouncedSearch(searchTerm);
  }, [searchTerm, debouncedSearch]);

  // Function to search users
  const searchUsers = async (term) => {
    if (term.length === 0) return;
    
    setLoading(true);
    setError(null);
    setSearchPerformed(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`blockuser/search?q=${encodeURIComponent(term)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSearchResults(response.data.users || []);
    } catch (err) {
      console.error('Error searching users:', err);
      setError(err.response?.data?.error?.message || t('error_searching_users'));
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  // Function to block a user
  const blockUser = async (userId) => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      await axios.post('blockuser/block', 
        { user_id: userId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Refresh both lists
      fetchBlockedUsers();
      
      // Update the search results to reflect the change
      setSearchResults(prevResults => 
        prevResults.map(user => 
          user.id === userId ? { ...user, blocked_status: true } : user
        )
      );
      
      // Update missed class users list if showing
      if (showMissedClasses) {
        setMissedClassUsers(prevUsers => 
          prevUsers.map(user => 
            user.id === userId ? { ...user, blocked_status: true } : user
          )
        );
      }
    } catch (err) {
      console.error('Error blocking user:', err);
      setError(err.response?.data?.error?.message || t('error_blocking_user'));
    } finally {
      setLoading(false);
    }
  };

  // Function to unblock a user
  const unblockUser = async (userId) => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      await axios.post('blockuser/unblock', 
        { user_id: userId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Refresh both lists
      fetchBlockedUsers();
      
      // Update the search results to reflect the change
      setSearchResults(prevResults => 
        prevResults.map(user => 
          user.id === userId ? { ...user, blocked_status: false } : user
        )
      );
      
      // Update missed class users list if showing
      if (showMissedClasses) {
        setMissedClassUsers(prevUsers => 
          prevUsers.map(user => 
            user.id === userId ? { ...user, blocked_status: false } : user
          )
        );
      }
    } catch (err) {
      console.error('Error unblocking user:', err);
      setError(err.response?.data?.error?.message || t('error_unblocking_user'));
    } finally {
      setLoading(false);
    }
  };

  // Helper function to render the user table rows with proper data attributes for mobile
  const renderUserRows = (users, showActions = true) => {
    return users.map(user => (
      <tr key={user.id} className={user.blocked_status ? 'user-blocked' : ''}>
        <td data-label={t('name')}>{user.name}</td>
        <td data-label={t('email')}>{user.email}</td>
        <td data-label={t('phone')}>{user.phone || t('not_provided')}</td>
        {showActions ? (
          <>
            <td data-label={t('status')}>
              {user.is_admin ? (
                <span className="admin-badge">{t('admin')}</span>
              ) : user.blocked_status ? (
                <span className="blocked-badge">{t('blocked')}</span>
              ) : (
                <span className="active-badge">{t('active')}</span>
              )}
            </td>
            <td data-label={t('actions')}>
              {user.is_admin ? (
                <span className="cannot-block">{t('cannot_block_admin')}</span>
              ) : user.blocked_status ? (
                <button 
                  className="unblock-button mobile-friendly-button" 
                  onClick={() => unblockUser(user.id)}
                  disabled={loading}
                >
                  {t('unblock')}
                </button>
              ) : (
                <button 
                  className="block-button mobile-friendly-button" 
                  onClick={() => blockUser(user.id)}
                  disabled={loading}
                >
                  {t('block')}
                </button>
              )}
            </td>
          </>
        ) : (
          <td data-label={t('actions')}>
            <button 
              className="unblock-button mobile-friendly-button" 
              onClick={() => unblockUser(user.id)}
              disabled={loading}
            >
              {t('unblock')}
            </button>
          </td>
        )}
      </tr>
    ));
  };

  // Helper function to render the missed class user table rows
  const renderMissedClassRows = (users) => {
    return users.map(user => {
      const missedCount = user.missed_class || 0;
      const missedCountLabel = missedCount >= 10 ? '10+' : missedCount.toString();
      
      return (
        <tr 
          key={user.id} 
          className={user.blocked_status ? 'user-blocked' : ''}
          data-missed-class-count={missedCountLabel}
        >
          <td data-label={t('name')}>{user.name}</td>
          <td data-label={t('email')}>{user.email}</td>
          <td data-label={t('phone')}>{user.phone || t('not_provided')}</td>
          <td data-label={t('missed_classes')}>{missedCount}</td>
          <td data-label={t('status')}>
            {user.is_admin ? (
              <span className="admin-badge">{t('admin')}</span>
            ) : user.blocked_status ? (
              <span className="blocked-badge">{t('blocked')}</span>
            ) : (
              <span className="active-badge">{t('active')}</span>
            )}
          </td>
          <td data-label={t('actions')}>
            {user.is_admin ? (
              <span className="cannot-block">{t('cannot_block_admin')}</span>
            ) : user.blocked_status ? (
              <button 
                className="unblock-button mobile-friendly-button" 
                onClick={() => unblockUser(user.id)}
                disabled={loading}
              >
                {t('unblock')}
              </button>
            ) : (
              <button 
                className="block-button mobile-friendly-button" 
                onClick={() => blockUser(user.id)}
                disabled={loading}
              >
                {t('block')}
              </button>
            )}
          </td>
        </tr>
      );
    });
  };

  // Set the active tab
  const setActiveTab = (tab) => {
    if (tab === 'search') {
      setShowBlocked(false);
      setShowMissedClasses(false);
    } else if (tab === 'blocked') {
      setShowBlocked(true);
      setShowMissedClasses(false);
    } else if (tab === 'missed') {
      setShowBlocked(false);
      setShowMissedClasses(true);
    }
  };

  return (
    <div className="user-block-management">
      <h2>{t('user_block_management')}</h2>
      
      {error && <div className="error-message">{error}</div>}
      
      <div className="toggle-view">
        <button 
          className={`toggle-button ${!showBlocked && !showMissedClasses ? 'active' : ''}`} 
          onClick={() => setActiveTab('search')}
        >
          {t('search_users')}
        </button>
        <button 
          className={`toggle-button ${showBlocked ? 'active' : ''}`} 
          onClick={() => setActiveTab('blocked')}
        >
          {t('blocked_users')} ({blockedUsers.length})
        </button>
        <button 
          className={`toggle-button ${showMissedClasses ? 'active' : ''}`} 
          onClick={() => setActiveTab('missed')}
        >
          {t('missed_classes')}
        </button>
      </div>

      {!showBlocked && !showMissedClasses ? (
        <div className="search-container">
          <div className="search-section">
            <div className="search-form">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={t('search_by_name_email_or_phone')}
                className="search-input"
              />
              {loading && <div className="search-loading">{t('searching')}...</div>}
            </div>
            <p className="search-hint">{t('search_hint')}</p>
          </div>

          {searchPerformed && (
            <div className="search-results">
              <h3>{t('search_results')} ({searchResults.length})</h3>
              
              {searchResults.length === 0 ? (
                <p className="no-results">{t('no_users_found')}</p>
              ) : (
                <div className="responsive-table-container">
                  <table className="user-table">
                    <thead>
                      <tr>
                        <th>{t('name')}</th>
                        <th>{t('email')}</th>
                        <th>{t('phone')}</th>
                        <th>{t('status')}</th>
                        <th>{t('actions')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {renderUserRows(searchResults)}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      ) : showBlocked ? (
        <div className="blocked-users-section">
          <h3>{t('blocked_users')}</h3>
          
          {loading ? (
            <p className="loading-indicator">{t('loading')}</p>
          ) : blockedUsers.length === 0 ? (
            <p className="no-results">{t('no_blocked_users')}</p>
          ) : (
            <div className="responsive-table-container">
              <table className="user-table">
                <thead>
                  <tr>
                    <th>{t('name')}</th>
                    <th>{t('email')}</th>
                    <th>{t('phone')}</th>
                    <th>{t('actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {renderUserRows(blockedUsers, false)}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : (
        <div className="missed-classes-section">
          <h3>{t('users_with_missed_classes')}</h3>
          
          {loading ? (
            <p className="loading-indicator">{t('loading')}</p>
          ) : missedClassUsers.length === 0 ? (
            <p className="no-results">{t('no_missed_class_users')}</p>
          ) : (
            <div className="responsive-table-container">
              <table className="user-table">
                <thead>
                  <tr>
                    <th>{t('name')}</th>
                    <th>{t('email')}</th>
                    <th>{t('phone')}</th>
                    <th>{t('missed_classes')}</th>
                    <th>{t('status')}</th>
                    <th>{t('actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {renderMissedClassRows(missedClassUsers)}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default UserBlockManagement; 